/* jshint esversion: 11, module: true, browser: true */

import { abilitiesPlugin } from '@casl/vue';
import Aura from '@primeuix/themes/aura';
import PortalVue from 'portal-vue';
import PrimeVue from 'primevue/config';
import ConfirmationService from 'primevue/confirmationservice';
import StyleClass from 'primevue/styleclass';
import ToastService from 'primevue/toastservice';
import VOffline from 'v-offline';
import { createApp, h, Transition, TransitionGroup } from 'vue';
import VueFullscreen from 'vue-fullscreen';
import VueHtmlToPaper from 'vue-html-to-paper';
import VueObserveVisibilityPlugin from 'vue-observe-visibility';
import VueScrollTo from 'vue-scrollto';
import App from '@/components/App.vue';
import registerGlobalComponents from '@/components/utilities/Index.js';
import { handleResponseError } from '@/Helpers.js';
import sentryMixin from '@/mixins/sentryMixin.js';
import vue3TreeselectCompatibilityMixin from '@/mixins/vue3TreeselectCompatibility.mixin.js';
import AmplitudePlugin from '@/plugins/amplitude.js';
import UtilitiesPlugin from '@/plugins/app-utilities.js';
import axios from '@/plugins/axios.js';
import VueFroalaPlugin from '@/plugins/froala-editor/froala.js';
import i18n from '@/plugins/i18n.js';
import { socketConnectionOptions } from '@/plugins/socketConnectionOptions.js';
import VueGatePlugin from '@/plugins/vue-gate/Index.js';
import moment from '@/plugins/vue-moment.js';
import VuePluralizePlugin from '@/plugins/vue-pluralize.js';
import SentryPlugin from '@/plugins/vue-sentry.js';
import VueSocket from '@/plugins/vue-socket.js';
import router, { afterEachSetup, beforeEachSetup } from '@/router/Index.js';
import DialogService from '@/services/DialogService.js';
import LoadingService from '@/services/LoadingService.js';
import ModalService from '@/services/ModalService.js';
import NotificationService from '@/services/NotificationService.js';
import '@/services/SessionMonitor.js';
import SnackbarService from '@/services/SnackbarService.js';
import EdverumToastService from '@/services/ToastService.js';
import { ability } from '@/store/Ability.js';
import store from '@/store/Index.js';
import safePrimeTooltipDirective from '@/utilities/safePrimeTooltipDirective.js';

import '../scss/main-primevue.scss';
import '../css/tailwind.css';
import '../scss/styles.scss';
import 'vue3-treeselect/dist/vue3-treeselect.css';
import { definePreset } from '@primeuix/themes';
import { ACCESSIBLE_FORM_FIELD_LIGHT, DEFAULT_PRIMARY, getPresetExt } from '@/layouts/theme/ColorThemes.js';

const options = {
    name: '_blank',
    specs: ['fullscreen=yes', 'titlebar=yes', 'scrollbars=yes'],
    styles: [
        'https://maxcdn.bootstrapcdn.com/bootstrap/4.0.0/css/bootstrap.min.css',
        'node_modules/froala-editor/css/froala_editor.pkgd.min.css',
    ],
    timeout: 1000,
    autoClose: true,
    windowTitle: window.document.title,
};

const app = createApp(App);

registerGlobalComponents(app);
app.directive('tooltip', safePrimeTooltipDirective);
app.directive('styleclass', StyleClass);

// Register Vue 3 built-in components globally for vue3-treeselect compatibility
app.component('Transition', Transition);
app.component('TransitionGroup', TransitionGroup);

// Register global mixins
app.mixin(sentryMixin);
app.mixin(vue3TreeselectCompatibilityMixin);
// Register plugins
app.use(abilitiesPlugin, ability, { useGlobalProperties: true });
app.use(VueHtmlToPaper, options);
app.use(VueSocket, {
    ...socketConnectionOptions(import.meta.env),
    channelAuthorization: {
        endpoint: '/api/broadcasting/auth',
        headers: {
            'X-CSRF-TOKEN': window.csrf_token,
        },
    },
    authorizer: channel => ({
        authorize: (socketId, callback) =>
            axios
                .post(
                    '/api/broadcasting/auth',
                    {
                        socket_id: socketId,
                        channel_name: channel.name,
                    },
                    { withCredentials: true }
                )
                .then(response => callback(false, response.data))
                .catch(error => callback(true, error)),
    }),
});

app.use({
    install($app) {
        $app.config.globalProperties.$moment = moment;
    },
});
app.use(store);
app.use(router);
app.use(i18n);
router.beforeEach(beforeEachSetup(app));
router.afterEach(afterEachSetup(app));

// Register Sentry plugin with correct parameters
app.use(SentryPlugin, { router });

app.use(UtilitiesPlugin, { app, router, $i18n: i18n });
app.use(VueFroalaPlugin);
app.use(VuePluralizePlugin);
app.use(VueScrollTo);
app.use(VueGatePlugin, app);
app.use(PortalVue);
app.use(VueFullscreen);
app.use(VOffline);
app.use(VueObserveVisibilityPlugin);

// Derive the boot-time PrimeVue preset from the same `DEFAULT_PRIMARY` that
// `layout.js`'s watcher fallback and `getPresetExt`'s default param read.
// Keeping a single source of truth here prevents the watcher dead-spot bug
// (`?? DEFAULT_PRIMARY` would collapse "no saved data" and "user picked the
// default color" into the same value, so without alignment the watcher
// never re-fires for whichever color happens to match PrimeVue's boot
// color). We layer the project's text-mutedColor override on top of the
// derived preset.
const defaultPresetExt = getPresetExt(DEFAULT_PRIMARY);
const PrimeVuePreset = definePreset(Aura, {
    semantic: {
        ...defaultPresetExt.semantic,
        colorScheme: {
            ...defaultPresetExt.semantic.colorScheme,
            // Override Aura's default muted text (slate-500, ~4.76:1 on white)
            // with a darker, more legible slate-600 (~7:1). hoverMutedColor is
            // bumped to slate-700 so interactive muted text still darkens.
            light: {
                ...defaultPresetExt.semantic.colorScheme.light,
                formField: ACCESSIBLE_FORM_FIELD_LIGHT,
                text: {
                    mutedColor: '{slate.600}',
                    hoverMutedColor: '{slate.700}',
                },
            },
        },
    },
});
app.use(PrimeVue, {
    theme: {
        preset: PrimeVuePreset,
        options: {
            darkModeSelector: '.app-dark',
            cssLayer: {
                name: 'primevue',
                order: 'theme, base, primevue',
            },
        },
    },
});
app.use(ToastService);
app.use(SnackbarService);
app.use(EdverumToastService);
app.use(DialogService);
app.use(ConfirmationService);
app.use(LoadingService);
app.use(ModalService);
app.use(NotificationService);
app.use(AmplitudePlugin);

// Register global properties
app.config.globalProperties.$handleResponseError = handleResponseError;

// Vue 2 compatibility for vue3-treeselect
// Provide $createElement globally to handle render function compatibility
const createElementWrapper = (...args) => {
    return h(...args);
};
app.config.globalProperties.$createElement = createElementWrapper;

app.config.performance = import.meta.env.MODE !== 'production';

store.dispatch('hydrateInitialState');

app.mount('#app');
